import {
  Children,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { startOfDay, startOfMonth } from 'date-fns';
import { DayPicker, type DateRange, type DropdownProps } from 'react-day-picker';
import { cn } from '@/lib/utils';
import {
  DayPickerCaptionBounds,
  DayPickerCaptionDropdown,
} from '@/components/ui/day-picker-caption';
import {
  buildRangeModifiers,
  canApplyDraft,
  canClearDraft,
  canStepDisplayMonths,
  clampDisplayMonth,
  DISPLAY_MONTH_YEARS_BACK,
  maxDisplayMonth,
  minDisplayMonth,
  nextDraftAfterPick,
  normalizeAppliedRange,
  seedDraftFromValue,
  seedVisibleMonths,
  shouldPreviewOnHover,
  stepDisplayMonths,
  type LooseDateRange,
} from '@/lib/customDateRange';

export const PANEL_WIDTH = 560;
const DISPLAY_MONTH_YEARS_FORWARD = 10;
const ESTIMATED_PANEL_HEIGHT = 440;
const VIEWPORT_PADDING = 12;

const RANGE_ENDPOINT =
  'rounded-full !bg-brand !text-white !ring-0 hover:!bg-brand hover:!text-white';
const RANGE_MIDDLE =
  '!rounded-none !bg-brand-soft !text-brand-cta !ring-0 hover:!bg-brand-soft hover:!text-brand-cta';

const CAPTION_MENU = '[data-date-range-caption-menu]';
const DATE_RANGE_PANEL = '[data-date-range-panel]';

export function isDateRangeChrome(node: EventTarget | null): boolean {
  if (!(node instanceof Element)) return false;
  return Boolean(node.closest(`${DATE_RANGE_PANEL}, ${CAPTION_MENU}`));
}

const RANGE_DAY_CLASSNAMES = {
  months: 'min-w-0',
  month: 'min-w-0 space-y-0 px-2 py-1',
  caption: 'flex items-center justify-center pt-0',
  caption_dropdowns: 'mb-2 flex w-full items-center gap-1.5',
  dropdown_month: 'relative inline-flex min-w-0 flex-1 items-center',
  dropdown_year: 'relative inline-flex w-[84px] shrink-0 items-center',
  vhidden: 'sr-only',
  nav: 'hidden',
  nav_button: 'hidden',
  table: 'w-full border-collapse',
  head_row: 'mb-1 flex w-full',
  head_cell:
    'flex h-auto w-[calc(100%/7)] items-center justify-center py-1 text-[11px] font-semibold text-[#98A2B3]',
  row: 'mt-0 flex w-full',
  cell: 'relative h-8 w-[calc(100%/7)] p-0 text-center text-[12.5px] focus-within:relative focus-within:z-20',
  day: cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-full p-0',
    'cursor-pointer text-[12.5px] font-medium text-[#101828]',
    'hover:bg-[#F2F4F7]',
    'outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
  ),
  day_today: 'text-brand-cta ring-1 ring-inset ring-[#ABEFC6] [[aria-selected]_&]:ring-0',
  day_disabled: '!cursor-not-allowed text-[#D0D5DD] opacity-100 hover:bg-transparent',
  day_outside: 'invisible',
  day_hidden: 'invisible',
} as const;

const RANGE_MODIFIER_CLASS_NAMES = {
  range_start: RANGE_ENDPOINT,
  range_end: RANGE_ENDPOINT,
  range_middle: RANGE_MIDDLE,
  preview_start: RANGE_ENDPOINT,
  preview_end: RANGE_ENDPOINT,
  preview_middle: RANGE_MIDDLE,
};

type RangeMonthCalendarProps = {
  month: Date;
  onMonthChange: (month: Date) => void;
  today: Date;
  fromMonth: Date;
  fromYear: number;
  rangeModifiers: ReturnType<typeof buildRangeModifiers>;
  onDayClick: (day: Date, mods: { disabled?: boolean }) => void;
  onDayMouseEnter: (day: Date, mods: { disabled?: boolean }) => void;
};

function RangeMonthCalendar({
  month,
  onMonthChange,
  today,
  fromMonth,
  fromYear,
  rangeModifiers,
  onDayClick,
  onDayMouseEnter,
}: RangeMonthCalendarProps) {
  const captionBounds = useMemo(
    () => ({ month, min: fromMonth, max: today }),
    [month, fromMonth, today],
  );

  return (
    <DayPickerCaptionBounds bounds={captionBounds}>
    <DayPicker
      numberOfMonths={1}
      month={month}
      onMonthChange={onMonthChange}
      onDayClick={onDayClick}
      onDayMouseEnter={onDayMouseEnter}
      disabled={{ after: today }}
      fromMonth={fromMonth}
      toDate={today}
      showOutsideDays={false}
      captionLayout="dropdown"
      fromYear={fromYear}
      modifiers={rangeModifiers}
      modifiersClassNames={RANGE_MODIFIER_CLASS_NAMES}
      className="p-0"
      classNames={RANGE_DAY_CLASSNAMES}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
        Dropdown: DayPickerCaptionDropdown,
      }}
    />
    </DayPickerCaptionBounds>
  );
}

export type CustomDateRangeValue = LooseDateRange;

export type CustomDateRangePanelProps = {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  value?: CustomDateRangeValue;
  onApply: (range: { from: Date; to: Date }) => void;
  onCancel: () => void;
  onClear?: () => void;
  clearEnabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  title?: string;
  testIdPrefix?: string;
  /**
   * Where the calendar opens relative to its anchor: beside it (`side`, the
   * default — to the right, flipping left when there is no room) or directly
   * below it (`below`, left-aligned and kept inside the viewport).
   */
  placement?: 'side' | 'below';
};

export const CustomDateRangePanel = forwardRef<HTMLDivElement, CustomDateRangePanelProps>(
  function CustomDateRangePanel(
    {
      open,
      anchorRef,
      value,
      onApply,
      onCancel,
      onClear,
      clearEnabled = false,
      onMouseEnter,
      onMouseLeave,
      title = 'Custom date range',
      testIdPrefix = 'custom-date-range',
      placement = 'side',
    },
    ref,
  ) {
    const [draft, setDraft] = useState<DateRange | undefined>();
    const [hoverDate, setHoverDate] = useState<Date | undefined>();
    const [leftMonth, setLeftMonth] = useState<Date>(() => seedVisibleMonths(undefined, new Date()).left);
    const [rightMonth, setRightMonth] = useState<Date>(
      () => seedVisibleMonths(undefined, new Date()).right,
    );
    const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(
      null,
    );

    const today = useMemo(() => startOfDay(new Date()), []);
    const currentYear = today.getFullYear();
    const monthBounds = useMemo(
      () => ({ min: minDisplayMonth(today), max: maxDisplayMonth(today) }),
      [today],
    );

    useEffect(() => {
      if (!open) return;
      setHoverDate(undefined);
      setDraft(seedDraftFromValue(value, today));
      const visible = seedVisibleMonths(value, today);
      setLeftMonth(visible.left);
      setRightMonth(visible.right);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, value?.from?.getTime(), value?.to?.getTime(), today.getTime()]);

    useLayoutEffect(() => {
      if (!open || !anchorRef.current) {
        setPanelPos(null);
        return;
      }
      const place = () => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        const gap = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(PANEL_WIDTH, Math.max(280, vw - VIEWPORT_PADDING * 2));
        let left: number;
        let top: number;
        const height = Math.min(ESTIMATED_PANEL_HEIGHT, vh - VIEWPORT_PADDING * 2);
        const fitsBelow = rect.bottom + gap + height <= vh - VIEWPORT_PADDING;
        const fitsAbove = rect.top - gap - height >= VIEWPORT_PADDING;
        const besideLeft = rect.right + gap + width <= vw - VIEWPORT_PADDING
          ? rect.right + gap
          : rect.left - gap - width >= VIEWPORT_PADDING
            ? rect.left - gap - width
            : null;
        if (placement === 'below' && (fitsBelow || fitsAbove || besideLeft === null)) {
          left = Math.max(VIEWPORT_PADDING, Math.min(rect.left, vw - width - VIEWPORT_PADDING));
          top = fitsBelow || !fitsAbove ? rect.bottom + gap : rect.top - gap - height;
        } else if (placement === 'below' && besideLeft !== null) {
          left = besideLeft;
          top = rect.top;
        } else {
          left = rect.right + gap;
          if (left + width > vw - VIEWPORT_PADDING) {
            left = rect.left - width - gap;
          }
          if (left < VIEWPORT_PADDING) {
            left = Math.max(VIEWPORT_PADDING, Math.min(rect.left, vw - width - VIEWPORT_PADDING));
          }
          top = rect.top;
        }
        if (top + ESTIMATED_PANEL_HEIGHT > vh - VIEWPORT_PADDING) {
          top = Math.max(VIEWPORT_PADDING, vh - ESTIMATED_PANEL_HEIGHT - VIEWPORT_PADDING);
        }
        setPanelPos({ top, left, width });
      };
      place();
      window.addEventListener('resize', place);
      window.addEventListener('scroll', place, true);
      return () => {
        window.removeEventListener('resize', place);
        window.removeEventListener('scroll', place, true);
      };
    }, [open, anchorRef, placement]);

    const canApply = canApplyDraft(draft);
    const canClear = canClearDraft(draft, clearEnabled);

    const rangeModifiers = useMemo(() => buildRangeModifiers(draft, hoverDate), [draft, hoverDate]);

    const pickDay = (day: Date) => {
      setHoverDate(undefined);
      setDraft((prev) => nextDraftAfterPick(prev, day));
    };

    const applyDraft = () => {
      const range = normalizeAppliedRange(draft);
      if (!range) return;
      setHoverDate(undefined);
      onApply(range);
    };

    const cancelDraft = () => {
      setHoverDate(undefined);
      setDraft(seedDraftFromValue(value, today));
      onCancel();
    };

    const clearDraft = () => {
      setDraft(undefined);
      setHoverDate(undefined);
      onClear?.();
    };

    const clampMonth = (candidate: Date) => clampDisplayMonth(candidate, monthBounds);

    const stepMonth = (delta: number) => {
      const next = stepDisplayMonths(leftMonth, rightMonth, delta, monthBounds);
      if (!next) return;
      setLeftMonth(next.left);
      setRightMonth(next.right);
    };

    const atMaxMonth = !canStepDisplayMonths(leftMonth, rightMonth, 1, monthBounds);
    const atMinMonth = !canStepDisplayMonths(leftMonth, rightMonth, -1, monthBounds);

    if (!open || !panelPos || typeof document === 'undefined') return null;

    /**
     * 🔴 `pointer-events-auto` ON THE PANEL IS LOAD-BEARING INSIDE A MODAL.
     *
     * This panel portals to document.body. When its opener sits inside a modal
     * Radix dialog (the social Overview's FloatingDrawer), Radix sets
     * `pointer-events: none` on the body and re-enables it only inside the
     * dialog — so every click on this calendar was swallowed and no date could
     * be picked. The calendar rendered correctly; it just did not respond.
     *
     * Re-enabling it is safe on a normal page too, where the body is never
     * disabled, so the CRM/Ads/Commerce uses are unaffected. The month/year
     * dropdown needs nothing: it is a Radix Select, which manages its own
     * pointer-events layer.
     */
    return createPortal(
      <div
        ref={ref}
        data-testid={`${testIdPrefix}-custom-panel`}
        style={{
          top: panelPos.top,
          left: panelPos.left,
          width: panelPos.width,
          maxWidth: panelPos.width,
        }}
        data-date-range-panel=""
        className="pointer-events-auto fixed z-[80] overflow-hidden rounded-[12px] border border-[#E4E7EC] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.12)]"
        onMouseEnter={onMouseEnter}
        onMouseLeave={(e) => {
          if (isDateRangeChrome(e.relatedTarget)) return;
          onMouseLeave?.();
        }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#EAECF0] px-4 py-3">
          <p className="text-[13px] font-semibold text-[#101828]">{title}</p>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label="Previous month"
              disabled={atMinMonth}
              onClick={() => stepMonth(-1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#667085] transition hover:bg-[#F2F4F7] hover:text-[#101828] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next month"
              disabled={atMaxMonth}
              onClick={() => stepMonth(1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#667085] transition hover:bg-[#F2F4F7] hover:text-[#101828] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className="relative grid grid-cols-2 divide-x divide-[#EAECF0] p-3"
          onMouseLeave={() => setHoverDate(undefined)}
        >
          <RangeMonthCalendar
            month={leftMonth}
            onMonthChange={(m) => setLeftMonth(clampMonth(m))}
            today={today}
            fromMonth={monthBounds.min}
            fromYear={currentYear - DISPLAY_MONTH_YEARS_BACK}
            rangeModifiers={rangeModifiers}
            onDayClick={(day, mods) => {
              if (mods.disabled) return;
              pickDay(day);
            }}
            onDayMouseEnter={(day, mods) => {
              if (mods.disabled) return;
              if (shouldPreviewOnHover(draft)) setHoverDate(startOfDay(day));
            }}
          />
          <RangeMonthCalendar
            month={rightMonth}
            onMonthChange={(m) => setRightMonth(clampMonth(m))}
            today={today}
            fromMonth={monthBounds.min}
            fromYear={currentYear - DISPLAY_MONTH_YEARS_BACK}
            rangeModifiers={rangeModifiers}
            onDayClick={(day, mods) => {
              if (mods.disabled) return;
              pickDay(day);
            }}
            onDayMouseEnter={(day, mods) => {
              if (mods.disabled) return;
              if (shouldPreviewOnHover(draft)) setHoverDate(startOfDay(day));
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-[#EAECF0] px-4 py-3">
          {onClear ? (
            <button
              type="button"
              data-testid={`${testIdPrefix}-custom-clear`}
              disabled={!canClear}
              onClick={clearDraft}
              className={cn(
                'inline-flex h-8 items-center rounded-lg px-2.5 text-[12.5px] font-semibold transition',
                canClear
                  ? 'text-[#667085] hover:bg-[#F2F4F7] hover:text-[#101828]'
                  : 'cursor-not-allowed text-[#D0D5DD]',
              )}
            >
              Clear
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancelDraft}
              className="inline-flex h-8 items-center rounded-lg border border-[#101828] bg-white px-2.5 text-[12.5px] font-semibold text-[#101828] transition hover:bg-[#F9FAFB]"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-custom-apply`}
              disabled={!canApply}
              onClick={applyDraft}
              className={cn(
                'inline-flex h-8 items-center rounded-lg px-2.5 text-[12.5px] font-semibold text-white transition',
                canApply
                  ? 'bg-brand hover:bg-brand-hover'
                  : 'cursor-not-allowed bg-[#98A2B3]',
              )}
            >
              Apply
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  },
);

/**
 * The same chrome as the range panel — styled month/year dropdowns, the two nav
 * arrows in a header strip, brand-green day pill — with ONE month and a single
 * selected day. Use it wherever a form asks for one date; the browser's own
 * `type="date"` calendar is unthemed.
 */
export function SingleDateCalendar({
  selected,
  onSelect,
  fromDate,
  toDate,
  className,
  testId,
}: {
  selected?: Date;
  onSelect: (day: Date) => void;
  fromDate?: Date;
  toDate?: Date;
  className?: string;
  testId?: string;
}) {
  const today = useMemo(() => startOfDay(new Date()), []);

  const bounds = useMemo(() => {
    const min = startOfMonth(fromDate ?? new Date(today.getFullYear() - DISPLAY_MONTH_YEARS_BACK, 0, 1));
    const max = startOfMonth(toDate ?? new Date(today.getFullYear() + DISPLAY_MONTH_YEARS_FORWARD, 11, 1));
    return { min, max };
  }, [fromDate, toDate, today]);

  const [month, setMonth] = useState<Date>(() =>
    clampDisplayMonth(selected ?? (fromDate && fromDate > today ? fromDate : today), bounds),
  );

  // Reopening on a field that already holds a date should land on that month,
  // not on wherever the last visit left the view.
  useEffect(() => {
    if (selected) setMonth(clampDisplayMonth(selected, bounds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.getTime()]);

  // No nav arrows: the month and year dropdowns in the caption are the only
  // way to move, which keeps the panel to a single row of chrome.

  const captionBounds = useMemo(
    () => ({ month, min: fromDate ?? bounds.min, max: toDate ?? bounds.max }),
    [month, fromDate, toDate, bounds],
  );

  return (
    <div
      data-testid={testId}
      data-date-range-panel=""
      className={cn(
        'w-[288px] overflow-hidden rounded-[12px] border border-[#E4E7EC] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.12)]',
        className,
      )}
    >
      <div className="p-3">
        <DayPickerCaptionBounds bounds={captionBounds}>
        <DayPicker
          mode="single"
          numberOfMonths={1}
          month={month}
          onMonthChange={(m) => setMonth(clampDisplayMonth(m, bounds))}
          selected={selected}
          onSelect={(day) => {
            if (day) onSelect(startOfDay(day));
          }}
          disabled={{ before: fromDate ?? undefined, after: toDate ?? undefined }}
          fromMonth={bounds.min}
          toMonth={bounds.max}
          fromYear={bounds.min.getFullYear()}
          toYear={bounds.max.getFullYear()}
          showOutsideDays={false}
          captionLayout="dropdown"
          className="p-0"
          classNames={RANGE_DAY_CLASSNAMES}
          modifiersClassNames={{ selected: RANGE_ENDPOINT }}
          components={{
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />,
            Dropdown: DayPickerCaptionDropdown,
          }}
        />
        </DayPickerCaptionBounds>
      </div>
    </div>
  );
}

const CLOSE_DELAY_MS = 180;

export function useCustomDateRangePanel() {
  const [open, setOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openPanel = useCallback(() => {
    clearCloseTimer();
    setShowPanel(true);
  }, [clearCloseTimer]);

  const closePanel = useCallback(() => {
    clearCloseTimer();
    setShowPanel(false);
  }, [clearCloseTimer]);

  const scheduleClosePanel = useCallback(() => {
    clearCloseTimer();
    if (isDateRangeChrome(document.activeElement)) return;
    if (document.querySelector(`${CAPTION_MENU}:hover`)) return;
    closeTimer.current = setTimeout(() => setShowPanel(false), CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const closeAll = useCallback(() => {
    clearCloseTimer();
    setShowPanel(false);
    setOpen(false);
  }, [clearCloseTimer]);

  useEffect(() => {
    if (!open) {
      clearCloseTimer();
      setShowPanel(false);
    }
  }, [open, clearCloseTimer]);

  useEffect(() => {
    if (!showPanel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showPanel, closePanel]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    open,
    setOpen,
    showPanel,
    menuRef,
    panelRef,
    openPanel,
    closePanel,
    scheduleClosePanel,
    closeAll,
    popoverContentProps: {
      onInteractOutside: (e: { target: EventTarget | null; preventDefault: () => void }) => {
        if (isDateRangeChrome(e.target)) e.preventDefault();
      },
    },
    customRowProps: {
      onMouseEnter: openPanel,
      onFocus: openPanel,
      onClick: openPanel,
      onMouseLeave: scheduleClosePanel,
    },
    presetRowProps: {
      onMouseEnter: () => {
        clearCloseTimer();
        setShowPanel(false);
      },
    },
    panelProps: {
      ref: panelRef,
      open: showPanel,
      anchorRef: menuRef,
      onMouseEnter: openPanel,
      onMouseLeave: scheduleClosePanel,
    },
  };
}
